/**
 * /admin/security — password change + sign-out-others + recent activity.
 *
 * Backed by the endpoints in ``backend/app/routers/admin_security.py``:
 *   GET  /api/admin/auth/sessions          — recent logins with isCurrent flag
 *   POST /api/admin/auth/sessions/revoke-others — bumps tokenVersion
 *   POST /api/admin/auth/password/change   — validate current → set new
 *   GET  /api/admin/auth/audit/recent      — last 20 auth events
 *
 * All four require an admin JWT. The two POST endpoints re-issue a fresh
 * cookie for THIS request so the caller stays logged in after the
 * tokenVersion bump.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatRelative, absoluteTimestamp } from "./backupUtils";

export function SecurityPage() {
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/auth/sessions"),
        api.get("/admin/auth/audit/recent"),
      ]);
      setSessions(s.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) {
      setBanner({ kind: "err", text: `Load failed: ${e?.response?.data?.detail?.message || e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onChangePassword = useCallback(async (e) => {
    e.preventDefault();
    setPwdBusy(true); setPwdMsg(null);
    try {
      await api.post("/admin/auth/password/change", {
        currentPassword: pwd.current,
        newPassword: pwd.next,
        confirmPassword: pwd.confirm,
      });
      setPwd({ current: "", next: "", confirm: "" });
      setPwdMsg({ kind: "ok", text: "Password changed. Every other device is now signed out." });
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object") ? (d.message || d.error) : (d || e?.message);
      setPwdMsg({ kind: "err", text: msg || "Password change failed." });
    } finally {
      setPwdBusy(false);
    }
  }, [pwd, refresh]);

  const onRevokeOthers = useCallback(async () => {
    setRevokeBusy(true); setBanner(null);
    try {
      const { data } = await api.post("/admin/auth/sessions/revoke-others");
      setShowRevokeConfirm(false);
      setBanner({ kind: "ok",
                  text: `Signed out every other device. Current token version: ${data.currentTokenVersion}.` });
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object") ? (d.message || d.error) : (d || e?.message);
      setBanner({ kind: "err", text: msg || "Revoke failed." });
    } finally {
      setRevokeBusy(false);
    }
  }, [refresh]);

  const revokeSpecific = useCallback(async (sessionId) => {
    setBanner(null);
    try {
      await api.post(`/admin/auth/sessions/${encodeURIComponent(sessionId)}/revoke`);
      setBanner({ kind: "ok", text: "Session revoked." });
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object") ? (d.message || d.error) : (d || e?.message);
      setBanner({ kind: "err", text: msg || "Revoke failed." });
    }
  }, [refresh]);

  return (
    <div data-testid="admin-security-page">
      <div className="admin-header">
        <div>
          <h1>Security &amp; sessions</h1>
          <p className="admin-header__sub">
            Rotate your admin password and sign out every other device.
            If you don&rsquo;t recognize a session&rsquo;s IP or device, revoke it and change your password immediately.
          </p>
        </div>
        <button
          type="button"
          className="backup-widget__btn"
          data-testid="security-refresh"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {banner && (
        <div
          className={`backup-page-banner backup-page-banner--${banner.kind}`}
          data-testid={`security-banner-${banner.kind}`}
        >
          {banner.text}
        </div>
      )}

      <div className="admin-grid-2">
        <section className="backup-card" data-testid="security-password-card">
          <h2>Change password</h2>
          <p className="admin-header__sub" style={{ marginBottom: "0.6rem" }}>
            Password must be ≥10 characters and include at least one letter and one digit.
            Changing password signs out every other device.
          </p>
          <form onSubmit={onChangePassword} style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600 }}>
              Current password
              <input
                type="password"
                value={pwd.current}
                onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                required
                data-testid="security-current-password"
                autoComplete="current-password"
                style={{ width: "100%", padding: "0.4rem 0.5rem", marginTop: "0.2rem",
                         borderRadius: 8, border: "1px solid var(--wmleye-line)" }}
              />
            </label>
            <label style={{ fontSize: "0.75rem", fontWeight: 600 }}>
              New password
              <input
                type="password"
                value={pwd.next}
                onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                required
                minLength={10}
                data-testid="security-new-password"
                autoComplete="new-password"
                style={{ width: "100%", padding: "0.4rem 0.5rem", marginTop: "0.2rem",
                         borderRadius: 8, border: "1px solid var(--wmleye-line)" }}
              />
            </label>
            <label style={{ fontSize: "0.75rem", fontWeight: 600 }}>
              Confirm new password
              <input
                type="password"
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                required
                data-testid="security-confirm-password"
                autoComplete="new-password"
                style={{ width: "100%", padding: "0.4rem 0.5rem", marginTop: "0.2rem",
                         borderRadius: 8, border: "1px solid var(--wmleye-line)" }}
              />
            </label>
            <button
              type="submit"
              className="backup-widget__btn backup-widget__btn--primary"
              data-testid="security-change-password-submit"
              disabled={pwdBusy}
              style={{ alignSelf: "flex-start" }}
            >
              {pwdBusy ? "Changing…" : "Change password"}
            </button>
            {pwdMsg && (
              <div
                className={`backup-widget__note backup-widget__note--${pwdMsg.kind}`}
                data-testid={`security-password-msg-${pwdMsg.kind}`}
              >
                {pwdMsg.text}
              </div>
            )}
          </form>
        </section>

        <section className="backup-card" data-testid="security-revoke-card"
                 style={{ borderLeft: "6px solid #c8402b" }}>
          <h2>Sign out other devices</h2>
          <p className="admin-header__sub" style={{ marginBottom: "0.6rem" }}>
            Invalidates every admin token except the one making this request.
            Every other tab / device gets a 401 on their next call and is forced to log in again.
          </p>
          {!showRevokeConfirm ? (
            <button
              type="button"
              className="backup-widget__btn backup-widget__btn--primary"
              style={{ background: "#c8402b", borderColor: "#c8402b" }}
              data-testid="security-revoke-others-btn"
              onClick={() => setShowRevokeConfirm(true)}
            >
              Sign out all other devices
            </button>
          ) : (
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <button
                type="button"
                className="backup-widget__btn backup-widget__btn--primary"
                style={{ background: "#c8402b", borderColor: "#c8402b" }}
                data-testid="security-revoke-others-confirm"
                onClick={onRevokeOthers}
                disabled={revokeBusy}
              >
                {revokeBusy ? "Revoking…" : "Confirm — sign them all out"}
              </button>
              <button
                type="button"
                className="backup-widget__btn"
                data-testid="security-revoke-others-cancel"
                onClick={() => setShowRevokeConfirm(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </section>
      </div>

      <section style={{ marginTop: "1.5rem" }}>
        <h2>Recent login activity</h2>
        <p className="admin-header__sub">
          Latest 20 auth events on this admin account (login success, login failed, password change, revoke).
        </p>
        <table className="admin-table" data-testid="security-audit-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>IP</th>
              <th>User-Agent</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 && !loading && (
              <tr><td colSpan="5" style={{ color: "var(--wmleye-ink-soft)" }}>No recent activity.</td></tr>
            )}
            {audit.map((ev) => {
              const isLogin = ev.action === "auth.login";
              const isCurr = sessions.find((s) => s.id === ev.id)?.isCurrent;
              return (
                <tr key={ev.id} data-testid={`security-audit-row-${ev.id}`}>
                  <td>
                    {formatRelative(ev.timestamp)}<br />
                    <small>{absoluteTimestamp(ev.timestamp)}</small>
                  </td>
                  <td>
                    <code>{ev.action}</code>
                    {isCurr && (
                      <span style={{ marginLeft: "0.4rem", padding: "0.05rem 0.35rem",
                                     background: "#e6f6ec", color: "#1f6d3f",
                                     borderRadius: 6, fontSize: "0.65rem", fontWeight: 700 }}
                            data-testid={`security-audit-row-current-${ev.id}`}>
                        This device
                      </span>
                    )}
                  </td>
                  <td><code>{ev.ipAddress || "?"}</code></td>
                  <td>
                    <small style={{ wordBreak: "break-all", maxWidth: 260, display: "inline-block" }}>
                      {ev.userAgent || "?"}
                    </small>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {isLogin && !isCurr && (
                      <button
                        type="button"
                        className="backup-widget__btn"
                        data-testid={`security-audit-row-revoke-${ev.id}`}
                        onClick={() => revokeSpecific(ev.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
